<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinDateInput from '@/components/atoms/DolphinDateInput.vue'
import {
  type DailyLessonStartViewKollegeUserFragment,
  useCreateKollegeExpenseRecordDailyLessonMutation,
} from './DailyLessonStartView.generated'
import { computed, ref } from 'vue'
import { format } from 'date-fns'
import { useAlert } from '@/libs/alertPortal'
import { useModal } from '@/hooks/useModal'
import { useRouter } from 'vue-router'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import { toTimeString } from '@/libs/utilities/gql-scalar'

const date = ref<Date | null>(null)
const props = defineProps<{
  user: DailyLessonStartViewKollegeUserFragment
}>()
const { mutate } = useCreateKollegeExpenseRecordDailyLessonMutation()
const router = useRouter()

const alert = useAlert()
const { ControlledModal, openModal, closeModal } = useModal()

const createDailyLesson = async () => {
  if (!props.user.standardCourse?.id) {
    alert.error('まだコースが開始されていません')
    closeModal()
    return
  }
  if (!date.value) {
    return
  }
  const result = await mutate({
    input: {
      userCourseGUID: props.user.standardCourse.id,
      startsAt: toTimeString(date.value),
    },
  })

  if (result?.errors) {
    alert.error(result.errors[0].message)
    return
  }

  alert.success('デイリーレッスンを開始しました。')

  // 開始日時や追加されたデイリーレッスン等を反映させるためにリロードしちゃう
  router.go(0)
}

const formattedDate = computed(() => {
  if (!date.value) return null
  return format(date.value, 'yyyy/MM/dd')
})
</script>
<template>
  <section :class="$style.main">
    <SectionTitle>デイリーレッスン</SectionTitle>
    <p>
      デイリーレッスンはありません。 <br />
      開始日を設定して、デイリーレッスンを開始してください。
    </p>

    <form :class="$style.form" @submit.prevent="openModal">
      <div :class="$style.form__labelWrapper">
        <label :class="$style.form__label">開始日</label>
        <DolphinDateInput
          :class="$style.form__input"
          v-model="date"
          format="yyyy/MM/dd"
        />
      </div>
      <FlatButton
        variant="primary"
        type="submit"
        :class="$style.button"
        :disabled="!date"
      >
        デイリーレッスンを開始
      </FlatButton>
    </form>
    <component :is="ControlledModal">
      <ConfirmationModal
        title="該当ユーザーのデイリーレッスンを開始"
        buttonText="デイリーレッスンを開始"
        @confirm="createDailyLesson"
        @close="closeModal"
      >
        <p>
          <b
            >{{ props.user.profile?.familyName
            }}{{ props.user.profile?.givenName }}</b
          >様のデイリーレッスンを<b>{{ formattedDate }}</b
          >に開始します。よろしいですか？<br />
          後から変更できませんので、注意してください。
        </p>
      </ConfirmationModal>
    </component>
  </section>
</template>

<style lang="scss" module>
.main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__labelWrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 16px;
    font-weight: bold;
    line-height: 24px;
    color: $color-text-and-object-high-emphasis;
    flex-grow: 2;
  }

  &__input {
    flex-grow: 0;
  }
}

.button {
  align-self: flex-end;
}
</style>
